<template>
  <div ref="treeVisual" class="visual side-visual tree-visual">
    <div style="margin: 0 auto" ref="tree">
      <div class="tree-wrapper" style="position: relative">
        <div
          class="tree-wrapper-inner"
          style="padding-bottom: 132.926829268%; width: 100%; position: relative"
        >
          <img
            ref="treeImage"
            class="tree"
            src="../../assets/sessions/tree-bare.png"
            alt="Gyre Leadership Tree"
            style="width: 100%; position: absolute"
          />

          <img
            ref="leaf1"
            src="../../assets/sessions/leaf-1.png"
            :style="leafStyle(60, 321, 66, 56)"
            alt=""
          />
          <img
            ref="leaf2"
            src="../../assets/sessions/leaf-2.png"
            :style="leafStyle(0, 208, 81, 69)"
            alt=""
          />

          <img
            ref="leaf3"
            src="../../assets/sessions/leaf-3.png"
            :style="leafStyle(24, 100, 81, 82)"
            alt=""
          />
          <img
            ref="leaf4"
            src="../../assets/sessions/leaf-4.png"
            :style="leafStyle(166, 0, 98, 110)"
            alt=""
          />
          <img
            ref="leaf5"
            src="../../assets/sessions/leaf-5.png"
            :style="leafStyle(258, 96, 105, 114)"
            alt=""
          />
          <img
            ref="leaf6"
            src="../../assets/sessions/leaf-6.png"
            :style="leafStyle(346, 240, 64, 49)"
            alt=""
          />
          <img
            ref="leaf7"
            src="../../assets/sessions/leaf-7.png"
            :style="leafStyle(283, 320, 66, 52)"
            alt=""
          />
          <img
            ref="leaf8"
            src="../../assets/sessions/leaf-8.png"
            :style="leafStyle(136, 247, 53, 61)"
            alt=""
          />

          <img
            ref="leaf9"
            src="../../assets/sessions/leaf-9.png"
            :style="leafStyle(122, 132, 70, 94)"
            alt=""
          />
        </div>
      </div>
      <div ref="rootsWrapper" class="roots-wrapper">
        <img
          src="../../assets/sessions/roots-black.png"
          alt="Gyre Leadership Roots"
          style="width: 170%; margin-left: -35%; margin-right: -35%"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { Power4, TimelineLite, TimelineMax } from "gsap";

function leafStyle(left: number, top: number, width: number, height: number): string {
  const treeW = 410;
  const treeH = 545;
  return `
    position: absolute;
    left: ${(left / treeW) * 100}%;
    top: ${(top / treeH) * 100}%;
    width: ${(width / treeW) * 100}%;
    height: ${(height / treeH) * 100}%;
    opacity: 0;
`;
}

// template refs
const treeVisual = ref<HTMLElement | null>(null);
const tree = ref<HTMLElement | null>(null);
const treeImage = ref<HTMLElement | null>(null);
const leaf1 = ref<HTMLElement | null>(null);
const leaf2 = ref<HTMLElement | null>(null);
const leaf3 = ref<HTMLElement | null>(null);
const leaf4 = ref<HTMLElement | null>(null);
const leaf5 = ref<HTMLElement | null>(null);
const leaf6 = ref<HTMLElement | null>(null);
const leaf7 = ref<HTMLElement | null>(null);
const leaf8 = ref<HTMLElement | null>(null);
const leaf9 = ref<HTMLElement | null>(null);
const rootsWrapper = ref<HTMLElement | null>(null);

const leaves = computed(() => {
  return [
    leaf1.value,
    leaf2.value,
    leaf3.value,
    leaf4.value,
    leaf5.value,
    leaf6.value,
    leaf7.value,
    leaf8.value,
    leaf9.value,
  ];
});

const enter = (tl: TimelineMax): void => {
  tl.to(treeVisual.value as HTMLElement, 0, { autoAlpha: 1, scale: 1 })
    .to(
      tree.value as HTMLElement,
      1,
      {
        autoAlpha: 1,
        ease: Power4.easeOut,
      },
      0,
    )
    .to(leaves.value, 0, { scale: 0 })
    .to(leaves.value, 1, { autoAlpha: 1, ease: Power4.easeOut, scale: 1 });
};

const leave = (tl: TimelineMax, scale?: number): void => {
  tl.to(
    treeVisual.value as HTMLElement,
    1,
    { autoAlpha: 0, ease: Power4.easeOut, scale: (scale ?? 1) * 0.8 },
    0,
  );
};

const fadeIn = (tl: TimelineMax, explicitScale?: number, explicitYPercent?: number): void => {
  const scale = explicitScale ?? 1;
  const yPercent = explicitYPercent ?? 0;
  tl.to(treeVisual.value as HTMLElement, 0, {
    scale: scale * 0.8,
    autoAlpha: 0,
    yPercent,
  })
    .to(leaves.value, 0, { scale: 1, autoAlpha: 1 })
    .to(
      treeVisual.value as HTMLElement,
      1,
      {
        autoAlpha: 1,
        ease: Power4.easeOut,
        scale,
        yPercent,
      },
      "+=0.1",
    );
};

const fadeOut = (tl: TimelineMax): TimelineLite =>
  tl.to(treeVisual.value as HTMLElement, 1, { autoAlpha: 0, ease: Power4.easeOut }, 0);

defineExpose({ enter, leave, fadeIn, fadeOut });
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less">
.session.session-wide .visual.tree-visual.tree-visual > * {
  max-width: 20vw;
}

.session.session-inline .visual.tree-visual.tree-visual > * {
  max-width: 50vw;
}

.session.session-inline .visual.tree-visual.tree-visual > * {
  @media (orientation: landscape) {
    max-width: 30vh;
  }
}

.tree-visual {
  filter: drop-shadow(0px 0px 3px rgba(60, 60, 60, 0.5));
}
</style>
